import type { PartitionKey } from '@backend/types';
import { NextGenBrokerRoles, UserRole } from '@root/@types/types';
import { z } from 'zod';

export const userSchema = z.object({
  email: z.string(),
  id: z.string(),
  name: z.string(),
  role: z.nativeEnum(UserRole),
  external_role: z.enum([...NextGenBrokerRoles, UserRole.TemplateManager]).optional(),
  tenant_id: z.string().uuid(),
  teams: z.string().optional(),
  team_id: z.string().optional(),
});

export const systemUserSchema = z.object({
  id: z.literal('system'),
  name: z.literal('system'),
  email: z.literal('system@system.com'),
  role: z.literal('system'),
  tenant_id: z.string().uuid(),
  teams: z.string().optional(),
  team_id: z.string().optional(),
});

export const baseAuthorizerSchema = z.object({
  sub: z.string(),
  email: z.string().email(),
  given_name: z.string(),
  family_name: z.string(),
  'custom:role': z.nativeEnum(UserRole),
  'custom:tenant_id': z.string().uuid(),
  'custom:teams': z.string().optional(),
});

export const pplAuthorizerSchema = baseAuthorizerSchema
  .extend({
    session_id: z.string(),
    alias_id: z.string(),
    external_role: z.enum([...NextGenBrokerRoles, UserRole.TemplateManager]),
    company_name: z.string(),
    organisation_name: z.string(),
    organisation_id: z.string(),
    team_id: z.string().optional(),
    team_name: z.string().optional(),
    correlation_id: z.string().optional(),
  })
  .omit({ 'custom:teams': true });

export const pplBrokerAuthorizerSchema = pplAuthorizerSchema.pick({
  alias_id: true,
  sub: true,
  email: true,
  'custom:role': true,
  'custom:tenant_id': true,
  team_id: true,
  correlation_id: true,
});

export const baseDatabaseSchema = z.object({
  created_at: z.string(),
  created_by: userSchema.partial({ tenant_id: true }).or(systemUserSchema),
  deleted_at: z.nullable(z.string()).optional(),
  deleted_by: userSchema.partial({ tenant_id: true }).or(systemUserSchema).optional(),
  id: z.string(),
  last_updated_by: userSchema.partial({ tenant_id: true }).or(systemUserSchema).optional(),
  pk: z.string(),
  sk: z.string(),
  updated_at: z.string().optional(),
  update_type: z.string().optional(),
});

export const tenantNameSchema = z.enum(['ct-ppl', 'ct-lockton', 'ct-bms', 'ct-ajg', 'ct-howden']);

export type PK = { pk: PartitionKey };
export type SK = { sk: string };
export type TenantName = z.infer<typeof tenantNameSchema>;
export type User = z.infer<typeof userSchema>;
export type SystemUser = z.infer<typeof systemUserSchema>;
export type DbResource = z.infer<typeof baseDatabaseSchema>;
export type BaseAuthorizer = z.infer<typeof baseAuthorizerSchema>;
export type PPLAuthorizer = z.infer<typeof pplAuthorizerSchema>;
export type PPLBrokerAuthorizer = z.infer<typeof pplBrokerAuthorizerSchema>;
